import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MOBILE_BREAKPOINT } from '@/lib/layout/layoutTokens';

export const APP_NAVIGATION_ID = 'app-primary-navigation';
export const APP_NAVIGATION_TOGGLE_ID = 'app-navigation-toggle';

const COLLAPSE_PREFERENCE_KEY = 'ax1.navigation.sidebar.collapsed';

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
}

function readCollapsePreference(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(COLLAPSE_PREFERENCE_KEY) === 'true';
}

/** Shared responsive behavior for every authenticated application shell. */
export function useNavigationShellState() {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readCollapsePreference);

  useEffect(() => {
    const onResize = () => {
      const nextIsMobile = isMobileViewport();
      setIsMobile(nextIsMobile);
      if (nextIsMobile) setIsMobileNavigationOpen(false);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    setIsMobileNavigationOpen(false);
  }, [isMobile, location.pathname, location.search]);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_PREFERENCE_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const dismissMobileNavigation = useCallback((restoreFocus = false) => {
    setIsMobileNavigationOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => {
        document.getElementById(APP_NAVIGATION_TOGGLE_ID)?.focus();
      });
    }
  }, []);

  useEffect(() => {
    if (!isMobile || !isMobileNavigationOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      dismissMobileNavigation(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dismissMobileNavigation, isMobile, isMobileNavigationOpen]);

  const toggleNavigation = useCallback(() => {
    if (isMobile) {
      setIsMobileNavigationOpen((open) => !open);
      return;
    }
    setIsSidebarCollapsed((collapsed) => !collapsed);
  }, [isMobile]);

  return {
    isMobile,
    isMobileNavigationOpen,
    isNavigationVisible: !isMobile || isMobileNavigationOpen,
    isSidebarCollapsed: isMobile ? false : isSidebarCollapsed,
    toggleNavigation,
    dismissMobileNavigation,
  };
}

export type NavigationShellState = ReturnType<typeof useNavigationShellState>;
